import { DateTime, Duration } from 'luxon';
import {
  Cost,
  DateAggregation,
  ChangeStatistic,
  Trendline,
  Entity,
} from '@backstage-community/plugin-cost-insights-common';

export interface ServiceDef {
  id: string;
  name: string;
  base: number; // Tages-Baseline in EUR
}

export interface TenantDef {
  id: string;
  name: string;
  services: ServiceDef[];
}

export interface ProductDef {
  id: string;
  name: string;
  share: number; // Anteil an den Kosten eines Tenants
}

export const TENANTS: TenantDef[] = [
  {
    id: 'commerce',
    name: 'Commerce',
    services: [
      { id: 'orders', name: 'orders', base: 2600 },
      { id: 'inventory', name: 'inventory', base: 1800 },
    ],
  },
  {
    id: 'device-swap',
    name: 'Device Swap',
    services: [
      { id: 'device-swap-api', name: 'device-swap-api', base: 3100 },
      { id: 'device-swap-worker', name: 'device-swap-worker', base: 2200 },
    ],
  },
  {
    id: 'device-status',
    name: 'Device Status',
    services: [
      { id: 'device-status-api', name: 'device-status-api', base: 2400 },
      { id: 'device-status-poller', name: 'device-status-poller', base: 2900 },
    ],
  },
];

export const PRODUCTS: ProductDef[] = [
  { id: 'computeEngine', name: 'Compute', share: 0.45 },
  { id: 'cloudStorage', name: 'Storage', share: 0.15 },
  { id: 'cloudDatabase', name: 'Database', share: 0.2 },
  { id: 'cloudNetwork', name: 'Network', share: 0.12 },
  { id: 'loadBalancer', name: 'Load Balancer', share: 0.08 },
];

const REGIONS = ['eu-central-1', 'eu-west-1'];

// Deterministischer PRNG (mulberry32). Gleicher seed -> gleiche Folge.
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Stabiler String-Hash -> seed.
export function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// 'R2/P30D/2026-06-24' -> { startDate, totalDays } (inklusive Tage).
export function parseIntervals(intervals: string): {
  startDate: DateTime;
  totalDays: number;
} {
  const [rep, period, end] = intervals.split('/');
  const repetitions = Number(rep.replace('R', '')) || 1;
  const periodDays = Math.round(Duration.fromISO(period).as('days'));
  const totalDays = periodDays * repetitions;
  const endDate = DateTime.fromISO(end);
  const startDate = endDate.minus({ days: totalDays - 1 });
  return { startDate, totalDays };
}

function sum(agg: DateAggregation[]): number {
  return agg.reduce((acc, d) => acc + d.amount, 0);
}

// Tagesreihe um baseline, mit Wochen-Saisonalitaet + leichtem Aufwaerts-Trend, seeded.
export function aggregationFor(
  intervals: string,
  baseline: number,
  seed: number,
): DateAggregation[] {
  const { startDate, totalDays } = parseIntervals(intervals);
  const rand = mulberry32(seed);
  const result: DateAggregation[] = [];
  for (let i = 0; i < totalDays; i++) {
    const date = startDate.plus({ days: i });
    const weekend = date.weekday >= 6 ? 0.7 : 1.0;
    const trend = 1 + (i / totalDays) * 0.15;
    const noise = 0.85 + rand() * 0.3;
    const amount = Math.max(1, Math.round(baseline * weekend * trend * noise));
    result.push({ date: date.toFormat('yyyy-MM-dd'), amount });
  }
  return result;
}

// Zweite Haelfte (aktuell) gegen erste Haelfte (vorher).
export function changeOf(aggregation: DateAggregation[]): ChangeStatistic {
  const half = Math.floor(aggregation.length / 2);
  const prev = sum(aggregation.slice(0, half));
  const curr = sum(aggregation.slice(half));
  return {
    ratio: prev === 0 ? 0 : (curr - prev) / prev,
    amount: curr - prev,
  };
}

// Lineare Regression mit x = Unix-Sekunden (passt zur Chart-X-Achse).
export function trendlineOf(aggregation: DateAggregation[]): Trendline {
  const xs = aggregation.map(d => DateTime.fromISO(d.date).toSeconds());
  const ys = aggregation.map(d => d.amount);
  const n = xs.length;
  const sx = xs.reduce((a, b) => a + b, 0);
  const sy = ys.reduce((a, b) => a + b, 0);
  const sxy = xs.reduce((acc, x, i) => acc + x * ys[i], 0);
  const sxx = xs.reduce((acc, x) => acc + x * x, 0);
  const denom = n * sxx - sx * sx;
  const slope = denom === 0 ? 0 : (n * sxy - sx * sy) / denom;
  const intercept = (sy - slope * sx) / n;
  return { slope, intercept };
}

// Cost inkl. Aufschluesselung nach Produkt und Service.
export function costFor(
  id: string,
  intervals: string,
  baseline: number,
  seed: number,
): Cost {
  const aggregation = aggregationFor(intervals, baseline, seed);
  // Inner groupedCosts ids are used directly by the chart as the breakdown
  // legend label, so we deliberately use the display name (e.g. "Compute"),
  // not the config key. The seed is still mixed from the stable p.id/s.id.
  const product: Cost[] = PRODUCTS.map(p => ({
    id: p.name,
    aggregation: aggregationFor(intervals, baseline * p.share, seed ^ hash(p.id)),
  }));
  const tenant = TENANTS.find(t => t.id === id);
  const project: Cost[] = (tenant?.services ?? []).map(s => ({
    id: s.name,
    aggregation: aggregationFor(intervals, s.base, seed ^ hash(s.id)),
  }));
  return {
    id,
    aggregation,
    change: changeOf(aggregation),
    trendline: trendlineOf(aggregation),
    groupedCosts: { product, project },
  };
}

// Product-Entity (Aggregation = [Vorperiode, aktuelle Periode]) mit Region-Breakdown.
export function entityOf(
  product: string,
  intervals: string,
  baseline: number,
  seed: number,
): Entity {
  const agg = aggregationFor(intervals, baseline, seed ^ hash(product));
  const half = Math.floor(agg.length / 2);
  const prev = sum(agg.slice(0, half));
  const curr = sum(agg.slice(half));
  const region: Entity[] = REGIONS.map((label, idx) => {
    const frac = idx === 0 ? 0.65 : 0.35;
    return {
      id: label,
      aggregation: [Math.round(prev * frac), Math.round(curr * frac)] as [
        number,
        number,
      ],
      entities: {},
      change: {
        ratio: prev === 0 ? 0 : (curr - prev) / prev,
        amount: Math.round((curr - prev) * frac),
      },
    };
  });
  return {
    id: product,
    aggregation: [prev, curr],
    entities: { region },
    change: { ratio: prev === 0 ? 0 : (curr - prev) / prev, amount: curr - prev },
  };
}
